import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';

import AddDrug from './AddDrug';
import {
  isEnableLoadingSelector,
  drugListSelector,
} from './selector';
import { deleteDrug, getDrugList, refreshDrugList } from './thunk';

import type { TableBaseColumnType, TableBaseRowType } from '@/components/TableBase/Body';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { FacilityJson } from '@/types/domain/childApply/entities/facilityEntity';
import type { DrugJson } from '@/types/domain/drug/entities/drugEntity';
import type { UserType } from '@/types/domain/user/redux';

import AdditionalLoading from '@/components/AdditionalLoading';
import Delete from '@/components/Delete';
import TableBase from '@/components/TableBase';
import DrugInfo from '@/domain/drug/component/DrugInfo';
import { toStringDateTimeDisplay } from '@/utils/date';

/** プロパティ */
export type Props = {
  /** ユーザー */
  user: UserType,
  /** 子ども */
  child: ChildJson,
  /** 施設リスト */
  facilityList: FacilityJson[],
};

const Drug: React.FC<Props> = ({ user, child, facilityList }) => {
  const dispatch = useDispatch();
  const isEnableLoading = useSelector(isEnableLoadingSelector);
  const [
    isOpen,
    setIsOpen,
  ] = React.useState<boolean>(false);
  const drugList = useSelector(drugListSelector);
  const [
    deleteDrugJson,
    setDeleteDrugJson,
  ] = React.useState<DrugJson | undefined>(undefined);
  const rows: TableBaseRowType[] = [];
  for (let i = 0; i < drugList.length; i += 1) {
    if (drugList[i].data.childId === child.childId) {
      rows.push({
        id: i.toString(),
        measureDateTime:
          toStringDateTimeDisplay(new Date(drugList[i].data.measureDateTime), false),
        drug: drugList[i].data.drugContent,
        type: drugList[i].data.measurerType,
      });
    }
  }
  const columns: TableBaseColumnType[] = [
    { id: 'measureDateTime', label: '日時', minWidth: 110 },
    { id: 'drug', label: '内容', minWidth: 60 },
  ];
  const actions = [
    {
      icon: <DeleteIcon />,
      title: '削除',
      action: (r: TableBaseRowType) => {
        const item = drugList[parseInt(r.id, 10)];
        setDeleteDrugJson(item);
      },
      disabled: (r: TableBaseRowType) => r.type !== 'app',
    },
  ];
  return (
    <div>
      <div>
        <TableBase
          rows={rows}
          columns={columns}
          actions={actions}
          addAction={() => setIsOpen(true)}
          refreshAction={() => dispatch(refreshDrugList())}
          isPagination={false}
          isShowFilter={false}
          isShowFooter={false}
        />
        <AdditionalLoading
          isEnableLoading={isEnableLoading}
          onClick={() => dispatch(getDrugList())}
        />
        <AddDrug
          isOpen={isOpen}
          close={() => setIsOpen(false)}
          child={child}
          user={user}
          facilityList={facilityList}
        />
        <Delete<DrugJson>
          comp={DrugInfo}
          data={deleteDrugJson}
          onDelete={(json) => {
            if (json) {
              dispatch(deleteDrug({
                data: json,
                facilityIds: facilityList.map((v) => v.data.facilityId),
              }));
            }
            setDeleteDrugJson(undefined);
          }}
          title="薬"
          content="以下の記録を削除しますか？"
        />
      </div>
    </div>
  );
};

export default Drug;
